import { useState } from "react";
import { ServerFnError } from "catmint/error";
import {
  throwClientSafeError,
  returnClientSafeError,
  throwUnsafeError,
} from "./actions.fn";

interface ErrorResult {
  label: string;
  thrown: boolean;
  message: string;
  statusCode?: number;
  data?: Record<string, unknown>;
}

export function ErrorDemo() {
  const [result, setResult] = useState<ErrorResult | null>(null);
  const [loading, setLoading] = useState<string | null>(null);

  async function handleThrowClientSafe() {
    setLoading("throwClientSafe");
    setResult(null);
    try {
      await throwClientSafeError();
      setResult({
        label: "throwClientSafeError",
        thrown: false,
        message: "No error (unexpected)",
      });
    } catch (err) {
      if (err instanceof ServerFnError) {
        setResult({
          label: "throwClientSafeError",
          thrown: true,
          message: err.message,
          statusCode: err.statusCode,
          data: err.data,
        });
      } else {
        setResult({
          label: "throwClientSafeError",
          thrown: true,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    } finally {
      setLoading(null);
    }
  }

  async function handleReturnClientSafe() {
    setLoading("returnClientSafe");
    setResult(null);
    try {
      const value = await returnClientSafeError();
      if (value instanceof ServerFnError) {
        setResult({
          label: "returnClientSafeError",
          thrown: false,
          message: value.message,
          statusCode: value.statusCode,
          data: value.data,
        });
      } else {
        setResult({
          label: "returnClientSafeError",
          thrown: false,
          message: JSON.stringify(value),
        });
      }
    } catch (err) {
      setResult({
        label: "returnClientSafeError",
        thrown: true,
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoading(null);
    }
  }

  async function handleThrowUnsafe() {
    setLoading("throwUnsafe");
    setResult(null);
    try {
      await throwUnsafeError();
      setResult({
        label: "throwUnsafeError",
        thrown: false,
        message: "No error (unexpected)",
      });
    } catch (err) {
      if (err instanceof ServerFnError) {
        setResult({
          label: "throwUnsafeError",
          thrown: true,
          message: err.message,
          statusCode: err.statusCode,
          data: err.data,
        });
      } else {
        setResult({
          label: "throwUnsafeError",
          thrown: true,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleThrowClientSafe}
          disabled={loading !== null}
          className="px-4 py-2 rounded bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-medium transition-colors text-sm"
        >
          {loading === "throwClientSafe"
            ? "Calling..."
            : "Throw ClientSafeError"}
        </button>
        <button
          type="button"
          onClick={handleReturnClientSafe}
          disabled={loading !== null}
          className="px-4 py-2 rounded bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-medium transition-colors text-sm"
        >
          {loading === "returnClientSafe"
            ? "Calling..."
            : "Return ClientSafeError"}
        </button>
        <button
          type="button"
          onClick={handleThrowUnsafe}
          disabled={loading !== null}
          className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-800 disabled:opacity-50 text-white font-medium transition-colors text-sm"
        >
          {loading === "throwUnsafe"
            ? "Calling..."
            : "Throw plain Error (sanitized)"}
        </button>
      </div>

      {result && (
        <div className="p-4 bg-white rounded border text-sm font-mono space-y-1">
          <div className="text-gray-500 text-xs font-sans mb-2">
            {result.label}() &mdash;{" "}
            {result.thrown ? (
              <span className="text-red-600 font-medium">
                caught via try/catch
              </span>
            ) : (
              <span className="text-orange-600 font-medium">
                received as return value
              </span>
            )}
          </div>
          <div>message: {result.message}</div>
          {result.statusCode !== undefined && (
            <div>statusCode: {result.statusCode}</div>
          )}
          {result.data !== undefined && (
            <div>data: {JSON.stringify(result.data)}</div>
          )}
        </div>
      )}
    </div>
  );
}
